// Česká slova pro Kolonii: skloňování surovin, čísla, karty, stavby.
import { pips, RESOURCES, type Bag, type Dev, type Res, type Terrain } from "../../../games/kolonie/meta.ts";

interface Forms {
  one: string; // 1 kov
  few: string; // 2–4 kovy
  many: string; // 5+ kovů
  acc: string; // chce 1 vodu
  ins: string; // sousedí s vodou
}

export const RES: Record<Res, Forms> = {
  kov: { one: "kov", few: "kovy", many: "kovů", acc: "kov", ins: "kovem" },
  sklo: { one: "sklo", few: "skla", many: "skel", acc: "sklo", ins: "sklem" },
  voda: { one: "voda", few: "vody", many: "vod", acc: "vodu", ins: "vodou" },
  jidlo: { one: "jídlo", few: "jídla", many: "jídel", acc: "jídlo", ins: "jídlem" },
  energie: { one: "energie", few: "energie", many: "energií", acc: "energii", ins: "energií" },
};

/** Krátký název suroviny (1. pád, jednotné číslo). */
export const resName = (r: Res) => RES[r].one;

export function resCount(n: number, r: Res, acc = false): string {
  const f = RES[r];
  if (n === 1) return `1 ${acc ? f.acc : f.one}`;
  if (n >= 2 && n <= 4) return `${n} ${f.few}`;
  return `${n} ${f.many}`;
}

/** „2 kovy, 1 voda“; prázdné = „nic“. */
export function bagText(bag: Bag | Record<Res, number>, acc = false): string {
  const parts = RESOURCES.filter((r) => (bag[r] ?? 0) > 0).map((r) => resCount(bag[r]!, r, acc));
  return parts.length ? parts.join(", ") : "nic";
}

export function bagTotal(bag: Bag | Record<Res, number>): number {
  return RESOURCES.reduce((n, r) => n + (bag[r] ?? 0), 0);
}

const NUM_NOM: Record<number, string> = { 2: "dvojka", 3: "trojka", 4: "čtyřka", 5: "pětka", 6: "šestka", 8: "osmička", 9: "devítka", 10: "desítka", 11: "jedenáctka", 12: "dvanáctka" };
const NUM_LOC: Record<number, string> = { 2: "dvojce", 3: "trojce", 4: "čtyřce", 5: "pětce", 6: "šestce", 8: "osmičce", 9: "devítce", 10: "desítce", 11: "jedenáctce", 12: "dvanáctce" };
const PIP_WORD: Record<number, string> = { 5: "velmi častá", 4: "častá", 3: "střední", 2: "vzácná", 1: "velmi vzácná" };

export type ProbStyle = "cislo" | "slovo" | "tecky";

/** Doplňek k číslu podle nastavení: „, častá“ nebo „, 4 tečky“. */
function probSuffix(n: number, style: ProbStyle): string {
  const p = pips(n);
  if (style === "slovo") return `, ${PIP_WORD[p]}`;
  if (style === "tecky") return `, ${p} ${p === 1 ? "tečka" : p <= 4 ? "tečky" : "teček"}`;
  return "";
}

export function numberNom(n: number, style: ProbStyle = "cislo"): string {
  return `${NUM_NOM[n] ?? n}${probSuffix(n, style)}`;
}

export function numberLoc(n: number, style: ProbStyle = "cislo"): string {
  return `na ${NUM_LOC[n] ?? n}${probSuffix(n, style)}`;
}

/** „kov na osmičce“ / „kráter“ (pro seznamy polí). */
export function hexText(h: { terrain: Terrain; number: number | null }, style: ProbStyle = "cislo"): string {
  if (h.terrain === "krater") return "kráter";
  return `${resName(h.terrain)} ${numberLoc(h.number!, style)}`;
}

/** „kovem na osmičce“ (pro „sousedí s …“). */
export function hexTextIns(h: { terrain: Terrain; number: number | null }, style: ProbStyle = "cislo"): string {
  if (h.terrain === "krater") return "kráterem";
  return `${RES[h.terrain].ins} ${numberLoc(h.number!, style)}`;
}

export const DEV_NAME: Record<Dev, string> = {
  hlidka: "hlídková loď",
  tunely: "stavba tunelů",
  zasoby: "zásoby",
  monopol: "monopol",
  bod: "vítězný bod",
};

export const DEV_HELP: Record<Dev, string> = {
  hlidka: "přesune piráty a ukradne 1 kartu; 3 lodě dají největší flotilu",
  tunely: "postavíš 2 tunely zdarma",
  zasoby: "vezmeš si 2 libovolné suroviny z banky",
  monopol: "všichni ti dají všechny karty jedné suroviny",
  bod: "1 vítězný bod, ostatní ho nevidí",
};

export const BUILD_NAME = { tunel: "tunel", zakladna: "základna", mesto: "město", vyzkum: "karta výzkumu" } as const;

export function cards(n: number): string {
  return n === 1 ? "1 karta" : n >= 2 && n <= 4 ? `${n} karty` : `${n} karet`;
}

export function pointsText(n: number): string {
  return n === 1 ? "1 bod" : n >= 2 && n <= 4 ? `${n} body` : `${n} bodů`;
}

export function portText(type: Res | "any"): string {
  return type === "any" ? "stanice 3 za 1" : `stanice ${resName(type)} 2 za 1`;
}
